import React from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { HandshakeOutlined } from '@mui/icons-material';
import { Switch, SxProps, Theme, Typography } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import Constants from 'style/constants';

import { FeatureSubscriptionType } from 'features/feature-subscriptions/models/feature-subscription-type';
import styles from './feature-subscription-row.module.scss';

type Props = {
  index: number;
  isActive: boolean;
  featureSubscriptionType: FeatureSubscriptionType;
  isViewing: boolean;
  fieldNamePrefix: string;
  disabled?: boolean;
  tooltipText?: string;
};

const FeatureSubscriptionRow = ({
  index,
  isActive,
  featureSubscriptionType,
  isViewing,
  fieldNamePrefix,
  disabled = false,
  tooltipText = '',
}: Readonly<Props>): React.JSX.Element => {
  const { t } = useTranslation('featureSubscriptions');
  const { control } = useFormContext();
  const isActiveValue = useWatch({ control, name: `${fieldNamePrefix}.${index}.isActive`, defaultValue: isActive });

  const isDisabledOrValueIsNotActive = disabled || !isActiveValue;

  const getIconForSubscriptionType = (type: FeatureSubscriptionType, sx: SxProps<Theme>): React.JSX.Element => {
    if (type === FeatureSubscriptionType.CareConnect) {
      return <HandshakeOutlined sx={sx} />;
    }

    return <></>;
  };

  const iconSx: SxProps<Theme> = {
    color: isActiveValue ? Constants.Colors.primary : Constants.Colors.onSurfaceVariant,
    opacity: isDisabledOrValueIsNotActive ? Constants.Opacity.disabled : Constants.Opacity.full,
    transform: 'scaleX(-1)',
  };

  const textSx: SxProps<Theme> = {
    color: Constants.Colors.onSurfaceVariant,
    opacity: isDisabledOrValueIsNotActive ? Constants.Opacity.disabled : Constants.Opacity.full,
  };

  return (
    <div className={styles.container}>
      <Tooltip title={tooltipText}>
        <div className={styles.tooltipContent}>
          <Controller
            name={`${fieldNamePrefix}.${index}.isActive`}
            control={control}
            defaultValue={isActive}
            render={({ field }) => (
              <Switch
                checked={field.value ?? false}
                onChange={(_, checked) => field.onChange(checked)}
                onBlur={field.onBlur}
                disabled={isViewing || disabled}
              />
            )}
          />
          {getIconForSubscriptionType(featureSubscriptionType, iconSx)}
          <Typography variant="h5" className={styles.name} sx={textSx}>
            {t(`organisationSubscriptions.subscriptions.${featureSubscriptionType}`)}
          </Typography>
        </div>
      </Tooltip>
      <Typography variant="body2" sx={textSx}>
        {t(isActiveValue ? 'organisationSubscriptions.enabled' : 'organisationSubscriptions.disabled')}
      </Typography>
    </div>
  );
};

export default FeatureSubscriptionRow;
